import {AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, inject, Input, OnInit, ViewChild} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup,} from '@angular/forms';
import {BaseComponent, StringCustomValidatorContent, StringValidatorType, TemplateFeature,} from 'compob2b-libs';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {StringValidator} from '../../../../../../compob2b-admin/src/app/admin-shared/custom-validators/StringValidator';
import {
  TemplateFeatureService, TemplateFeatureValue
} from '../../../../../../compob2b-admin/src/app/cabinet/pim/product/create-update/features/template-feature.service'

@Component({
  selector: 'app-tf-string',
  templateUrl: './tf-string.component.html',
  styleUrls: ['./tf-string.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TfStringComponent extends BaseComponent implements OnInit, AfterViewInit {
  @Input() feature: TemplateFeature;
  @Input() featureLink = false;
  @ViewChild('inputTextArea') inputTextArea: ElementRef;

  private readonly maxTextAreaLength = this.environment.maxTextAreaLength;
  private readonly maxInputLength = this.environment.maxInputLength;

  isTextArea = false;
  public isLongContent = false;
  public trimString = true;

  form = new UntypedFormGroup({
    featureValue: new UntypedFormControl(null)
  });

  validatorName: string = null;
  typeValidator: StringValidatorType = null;
  customValidator: StringCustomValidatorContent = null;
  valueExample: string = null;

  templateFeatureService = inject(TemplateFeatureService);

  ngOnInit(): void {
    this.setValidator();

    this.form.get('featureValue').valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.unsubscribe),
      )
      .subscribe(data => {
        this.setFeature(data as string);
      });

    this.setDefaultValue();

    this.templateFeatureService.isFeaturesModelReloaded()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(_ => {
        this.setDefaultValue();
        this.cdr.detectChanges();
      });
  }

  private setValidator(): void {
    if (this.feature.validator !== null) {
      this.validatorName = this.feature.validator.header;
      this.typeValidator = this.feature.validator.content.stringValidator.typeValidator;
      this.valueExample = this.feature.validator.example;
      this.customValidator = this.feature.validator.content.stringValidator.customValidator;

      this.featureValue.addValidators(StringValidator.getValidator(this.feature.validator.content.stringValidator));
    }
  }

  ngAfterViewInit() {
    this.setFeature(this.form.get('featureValue').value);
  }

  private setDefaultValue(): void {
    const featureModel = this.templateFeatureService.getFeatureModel();

    if (this.feature.id in featureModel) {
      this.featureValue.setValue(featureModel[this.feature.id], {emitEvent: false});
    }

    this.setDefaultFeatureValue(this.featureValue.value);
  }

  private setFeature(value: String): void {
    value = value !== null && value.trim() !== '' ? value.trim() : null;

    this.isTextArea = value !== null && value.length > this.maxInputLength;
    this.isLongContent = value !== null && value.length > this.maxTextAreaLength;

    this.templateFeatureService.setFeature(
      this.feature.groupId,
      this.feature.id,
      value,
      this.form.valid,
      this.isShowEmptyWarning()
    );

    this.cdr.detectChanges();
    this.resizeTextAreaToFitContent(this.inputTextArea.nativeElement);
  }

  private setDefaultFeatureValue(featureValue: TemplateFeatureValue): void {
    this.templateFeatureService.setDefaultFeatureValue(
      this.feature.groupId,
      this.feature.id,
      featureValue,
      this.form.valid,
      this.isShowEmptyWarning()
    );
  }

  isEmpty(): boolean {
    return !this.featureValue.value;
  }

  isRequired(): boolean {
    return this.feature.required;
  }

  isShowEmptyWarning(): boolean {
    return this.isRequired() && this.isEmpty();
  }

  get featureValue(): any {
    return this.form.get('featureValue');
  }

  toggleTextarea() {
    this.trimString = !this.trimString;
  }

  resizeOnTextAreaInput(inputEvent: any) {
    this.resizeTextAreaToFitContent(inputEvent.target);
  }

  private resizeTextAreaToFitContent(nativeElement: any): void {
    if (nativeElement.tagName == 'INPUT') {
      return;
    }

    nativeElement.style.height = '0px';
    nativeElement.style.height = (nativeElement.scrollHeight + 3) + 'px';
  }
}
